import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmButton } from '@spartan-ng/helm/button';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { HlmIcon } from '@spartan-ng/helm/icon';
import { lucideArrowLeft, lucideSearch, lucideX, lucidePackage } from '@ng-icons/lucide';
import { PageHeaderComponent, InlineAlertComponent, SkeletonComponent } from '@shared/ui/components';
import { OrderService } from '../../services/order.service';
import { RepackingStateService } from '../../services/repacking-state.service';
import { ParcelDto, RepackUpdateRequestDto } from '../../../models/order.dto';

@Component({
  selector: 'app-repack-add-parcels',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ...HlmCardImports,
    HlmButton,
    NgIcon,
    HlmIcon,
    PageHeaderComponent,
    InlineAlertComponent,
    SkeletonComponent,
  ],
  providers: [provideIcons({ lucideArrowLeft, lucideSearch, lucideX, lucidePackage })],
  templateUrl: './repack-add-parcels.component.html',
  styleUrl: './repack-add-parcels.component.css'
})
export class RepackAddParcelsComponent implements OnInit {
  targetCartonNo: string = '';
  targetShopeeNo: string = '';
  targetServiceCode: string = '';
  
  availableParcels: ParcelDto[] = [];
  selectedParcels: ParcelDto[] = [];
  
  isLoading: boolean = false;
  isLoadingParcels: boolean = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  
  searchTerm: string = '';
  filteredParcels: ParcelDto[] = [];
  
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private orderService: OrderService,
    private repackingStateService: RepackingStateService
  ) {}
  
  ngOnInit(): void {
    // Get target carton info from route param
    this.route.params.subscribe(params => {
      this.targetCartonNo = params['cartonNo'];
      this.loadTargetCartonInfo();
    });
  }
  
  loadTargetCartonInfo(): void {
    // Try to get info from state service first
    const targetInfo = this.repackingStateService.getTargetCartonInfo();
    
    if (targetInfo && targetInfo.cartonNo === this.targetCartonNo) {
      this.targetShopeeNo = targetInfo.shopeeOrderNo || '';
      this.targetServiceCode = targetInfo.serviceCode || '';
      
      // Now load available parcels
      this.loadAvailableParcels();
    } else {
      // Fetch carton details from API
      this.isLoading = true;
      this.orderService.getOrderByCartonNo(this.targetCartonNo).subscribe({
        next: (response) => {
          if (response && response.data) {
            const carton = response.data;
            this.targetShopeeNo = carton.shopeeOrderNo;
            this.targetServiceCode = carton.serviceCode;
            
            // Store for later use
            this.repackingStateService.setTargetCartonInfo({
              cartonNo: this.targetCartonNo,
              shopeeOrderNo: this.targetShopeeNo,
              serviceCode: this.targetServiceCode,
              destinationRegion: carton.destinationRegion,
              status: carton.status,
            });
            
            // Now load available parcels
            this.loadAvailableParcels();
             if (carton.status === 'Cancelled') {
            this.successMessage = "Note: You are adding parcels to a cancelled carton. This will reactivate the carton.";
          }
          } else {
            this.errorMessage = "Could not retrieve target carton information.";
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.errorMessage = `Failed to load carton information: ${err.message}`;
          this.isLoading = false;
        }
      });
    }
  }
  
  loadAvailableParcels(): void {
    this.isLoadingParcels = true;
    
    // Get parcels that are not assigned to cartons (cancelled/unbound)
    this.orderService.getAvailableParcels(this.targetServiceCode).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.availableParcels = response.data;
          this.filteredParcels = [...this.availableParcels];
          console.log(`Loaded ${this.availableParcels.length} available parcels`);
        } else {
          this.availableParcels = [];
          this.filteredParcels = [];
          this.errorMessage = "No available parcels found.";
        }
        this.isLoadingParcels = false;
      },
      error: (err) => {
        this.errorMessage = `Failed to load available parcels: ${err.message}`;
        this.isLoadingParcels = false;
      }
    });
  }
  
  filterParcels(): void {
    if (!this.searchTerm.trim()) {
      this.filteredParcels = [...this.availableParcels];
    } else {
      const term = this.searchTerm.toLowerCase().trim();
      this.filteredParcels = this.availableParcels.filter(parcel => 
        (parcel.trackingNumber && parcel.trackingNumber.toLowerCase().includes(term)) ||
        (parcel.shopeeOrderNo && parcel.shopeeOrderNo.toLowerCase().includes(term))
      );
    }
  }
  
  toggleParcelSelection(parcel: ParcelDto): void {
    const idx = this.selectedParcels.findIndex(p => p.trackingNumber === parcel.trackingNumber);
    if (idx !== -1) {
      this.selectedParcels.splice(idx, 1);
    } else {
      this.selectedParcels.push(parcel);
    }
  }
  
  isSelected(parcel: ParcelDto): boolean {
    return this.selectedParcels.some(p => p.trackingNumber === parcel.trackingNumber);
  }
  
  onSubmit(): void {
    if (this.selectedParcels.length === 0) {
      this.errorMessage = "Please select at least one parcel to add.";
      return;
    }
    
    this.isLoading = true;
    this.errorMessage = null;
    this.successMessage = null;
    
    // Get target carton details
    this.orderService.getOrderByCartonNo(this.targetCartonNo).subscribe({
      next: (response) => {
        if (!response || !response.data) {
          this.errorMessage = "Could not retrieve target carton information.";
          this.isLoading = false;
          return;
        }
        
        const carton = response.data;
        
        // Create request with all required properties from RepackUpdateRequestDto
        const request: RepackUpdateRequestDto = {
          originalRepackedCartonNo: this.targetCartonNo,
          ilhShopeeNo: this.targetShopeeNo || '',
          destinationRegion: carton.destinationRegion || '',
          cartonWeight: carton.cartonWeight || 0,
          cartonLength: carton.cartonLength || 0,
          cartonWidth: carton.cartonWidth || 0,
          cartonHeight: carton.cartonHeight || 0,
          cartonVolume: carton.cartonVolume || 0,
          parcelActions: this.selectedParcels.map(parcel => ({
            parcelTrackingNumber: parcel.trackingNumber || '',
            actionType: 1 // 1 = Add
          })),
           reactivateIfCancelled: true
        };
        
        console.log("DEBUG: Submitting request:", JSON.stringify(request, null, 2));
        
        this.orderService.repackUpdate(request).subscribe({
          next: (response) => {
            console.log("DEBUG: Update response:", response);
            this.isLoading = false;
            this.successMessage = `Successfully added ${this.selectedParcels.length} parcel(s) to carton ${this.targetCartonNo}.`;
            
            // Clear selection
            this.selectedParcels = [];
            
            // Refresh available parcels after update
            setTimeout(() => this.loadAvailableParcels(), 1000);
            
            // Add a delay before navigating
            setTimeout(() => {
              this.router.navigate(['/dashboard/orders']);
            }, 2000);
          },
          error: (err) => {
            console.error("Error adding parcels:", err);
            this.isLoading = false;
            this.errorMessage = `Failed to add parcels to carton: ${err.message}`;
          }
        });
      },
      error: (err) => {
        this.errorMessage = `Failed to load carton information: ${err.message}`;
        this.isLoading = false;
      }
    });
  }
  
  goBack(): void {
    // Navigate back to orders list
    this.router.navigate(['/dashboard/orders']);
  }
}



